import { Card, Col, Row, Typography, List, Table, Space, Spin, Empty, Select } from "antd";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { OverviewWrapper } from "./Overview.style";
import {
  TeamOutlined,
  UserAddOutlined,
  FileDoneOutlined,
  DollarCircleOutlined,
  RightOutlined,
  BankOutlined,
  FolderOutlined,
  ProjectOutlined,
  FieldTimeOutlined,
  AuditOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Title, Text } = Typography;

const formatCurrency = (value) => {
  const numValue = parseFloat(value) || 0;
  if (numValue >= 10000000) {
    return `₹ ${(numValue / 10000000).toFixed(2)} Cr`;
  } else if (numValue >= 100000) {
    return `₹ ${(numValue / 100000).toFixed(2)} Lac`;
  } else if (numValue >= 1000) {
    return `₹ ${(numValue / 1000).toFixed(2)} K`;
  }
  return `₹ ${numValue.toFixed(0)}`;
};

const CHART_COLORS = ["#014c75", "#00d9a9", "#1890ff", "#722ed1", "#fa8c16", "#eb2f96", "#52c41a", "#faad14"];

const Overview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [approvalCounts, setApprovalCounts] = useState(null);
  const [topCustomers, setTopCustomers] = useState([]);
  const [hiringTrend, setHiringTrend] = useState([]);
  const [roleDistribution, setRoleDistribution] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  const filteredHiringTrend = hiringTrend.filter(item => item.year === selectedYear);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(API_CONST.GET_DASHBOARD_FULL, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setMetrics(response.data.metrics);
        setApprovalCounts(response.data.approvalCounts);
        setTopCustomers(response.data.topCustomers || []);
        setHiringTrend(response.data.hiringTrend || []);
        setRoleDistribution(response.data.roleDistribution || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const metricsData = metrics
    ? [
        {
          value: metrics.totalJobsCreated,
          label: "Total Jobs Created",
          icon: <FileDoneOutlined />,
          iconBg: "#fff7e6",
        },
        {
          value: metrics.totalTalentsHired,
          label: "Total Talents Hired",
          icon: <TeamOutlined />,
          iconBg: "#e6f7ff",
        },
        {
          value: metrics.totalCustomersOnboarded,
          label: "Total Customers Joined",
          icon: <BankOutlined />,
          iconBg: "#e6fffb",
        },
        {
          value: metrics.totalPartnersJoined,
          label: "Total Partners Onboarded",
          icon: <UserAddOutlined />,
          iconBg: "#f0f5ff",
        },
        {
          value: formatCurrency(metrics.totalRevenue),
          label: "Total Revenue",
          icon: <DollarCircleOutlined />,
          iconBg: "#f6ffed",
        },
        {
          value: formatCurrency(metrics.totalPayoutAmount),
          label: "Total Payout Amount",
          icon: <DollarCircleOutlined />,
          iconBg: "#fff1f0",
        },
        {
          value: formatCurrency(metrics.averageRevenuePerCustomer),
          label: "Average Revenue per Customer",
          icon: <DollarCircleOutlined />,
          iconBg: "#f9f0ff",
        },
        {
          value: formatCurrency(metrics.averageCostPerTalent),
          label: "Average Cost per Talent",
          icon: <DollarCircleOutlined />,
          iconBg: "#e6f7ff",
        },
      ]
    : [];

  const approvalsData = approvalCounts
    ? [
        {
          count: approvalCounts.customers + approvalCounts.partners,
          description: "New Users Pending",
          icon: <UserAddOutlined />,
          tab: "Users",
          iconBg: "#e6fffb",
        },
        {
          count: approvalCounts.talents,
          description: "Talent Profiles Pending",
          icon: <TeamOutlined />,
          tab: "Profiles",
          iconBg: "#fff7e6",
        },
        {
          count: approvalCounts.jobs,
          description: "Jobs Pending Approval",
          icon: <AuditOutlined />,
          tab: "Jobs",
          iconBg: "#f9f0ff",
        },
        {
          count: approvalCounts.engagements,
          description: "Engagements Pending",
          icon: <ProjectOutlined />,
          tab: "Engagements",
          iconBg: "#e6f7ff",
        },
        {
          count: approvalCounts.projects,
          description: "Projects Pending",
          icon: <FolderOutlined />,
          tab: "Projects",
          iconBg: "#fff1f0",
        },
        {
          count: approvalCounts.timesheets,
          description: "Timesheets Pending",
          icon: <FieldTimeOutlined />,
          tab: "Timesheet",
          iconBg: "#f6ffed",
        },
      ]
    : [];

  const topCustomersColumns = [
    {
      title: "Customer Name",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <Space>
          <BankOutlined style={{ color: "#1890ff" }} />
          <span style={{ fontWeight: 500 }}>{text}</span>
        </Space>
      ),
    },
    {
      title: "Talents Hired",
      dataIndex: "talentsHired",
      key: "talentsHired",
      render: (value) => <span style={{ textAlign: "center", display: "block" }}>{value}</span>,
    },
    {
      title: "Total Revenue",
      dataIndex: "totalRevenue",
      key: "totalRevenue",
      render: (value) => (
        <span style={{ fontWeight: 500, color: "#014c75" }}>
          {formatCurrency(value || 0)}
        </span>
      ),
    },
  ];

  const handleApprovalClick = (tab) => {
    if (tab === "Timesheet") {
      navigate("/admin/timesheet");
    } else {
      navigate("/admin/approval-process", { state: { activeTab: tab } });
    }
  };

  const handleViewAllApprovals = () => {
    navigate("/admin/approval-process");
  };

  const handleViewAllCustomers = () => {
    navigate("/admin/finance");
  };

  if (loading) {
    return (
      <OverviewWrapper>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
          <Spin size="large" />
        </div>
      </OverviewWrapper>
    );
  }

  return (
    <OverviewWrapper>
      <Typography.Title level={3} className="page-title">
        Dashboard
      </Typography.Title>

      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        {metricsData.map((item, key) => (
          <Col xs={24} sm={12} lg={6} key={key}>
            <Card
              style={{
                borderRadius: "8px",
                border: "1px solid #f0f0f0",
              }}
              styles={{ body: { padding: "20px" } }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ flex: 1 }}>
                  <Title
                    level={2}
                    style={{
                      margin: 0,
                      marginBottom: "8px",
                      color: "#014c75",
                      fontSize: "28px",
                      fontWeight: 600,
                    }}
                  >
                    {item.value}
                  </Title>
                  <Text
                    style={{
                      fontSize: "13px",
                      color: "#666",
                    }}
                  >
                    {item.label}
                  </Text>
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    color: "#1890ff",
                    background: item.iconBg,
                    width: "48px",
                    height: "48px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        <Col xs={24} lg={12}>
          <Card
            title="Approvals Pending"
            extra={
              <a
                onClick={handleViewAllApprovals}
                style={{
                  color: "#00d9a9",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                View All <RightOutlined style={{ fontSize: "12px" }} />
              </a>
            }
            style={{
              borderRadius: "8px",
              border: "1px solid #f0f0f0",
              height: "100%",
            }}
            styles={{ body: { padding: "12px" } }}
          >
            <List
              itemLayout="horizontal"
              dataSource={approvalsData}
              size="small"
              renderItem={(item) => (
                <List.Item
                  onClick={() => handleApprovalClick(item.tab)}
                  style={{
                    padding: "10px 12px",
                    cursor: "pointer",
                    borderRadius: "6px",
                    transition: "all 0.3s",
                  }}
                  className="approval-item"
                  extra={<RightOutlined style={{ color: "#999" }} />}
                >
                  <List.Item.Meta
                    avatar={
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "8px",
                          background: item.iconBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "18px",
                          color: "#1890ff",
                        }}
                      >
                        {item.icon}
                      </div>
                    }
                    title={
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <span
                          style={{
                            fontSize: "18px",
                            fontWeight: 600,
                            color: item.count > 0 ? "#014c75" : "#999",
                            marginRight: "12px",
                            minWidth: "30px",
                          }}
                        >
                          {item.count}
                        </span>
                        <span
                          style={{
                            fontSize: "13px",
                            color: "#666",
                            fontWeight: 400,
                          }}
                        >
                          {item.description}
                        </span>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title="Top Customers Hiring Talents"
            extra={
              <a
                onClick={handleViewAllCustomers}
                style={{
                  color: "#00d9a9",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                View All <RightOutlined style={{ fontSize: "12px" }} />
              </a>
            }
            style={{
              borderRadius: "8px",
              border: "1px solid #f0f0f0",
              height: "100%",
            }}
            styles={{ body: { padding: "16px" } }}
          >
            {topCustomers.length > 0 ? (
              <Table
                columns={topCustomersColumns}
                dataSource={topCustomers.map((c, i) => ({ ...c, key: i }))}
                pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
                size="small"
                style={{
                  fontSize: "14px",
                }}
              />
            ) : (
              <Empty description="No hiring data available" />
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title="Monthly Hiring Trend"
            extra={
              <Select
                value={selectedYear}
                onChange={setSelectedYear}
                style={{ width: 100 }}
                size="small"
              >
                {yearOptions.map(year => (
                  <Select.Option key={year} value={year}>{year}</Select.Option>
                ))}
              </Select>
            }
            style={{
              borderRadius: "8px",
              border: "1px solid #f0f0f0",
              height: "100%",
            }}
            styles={{ body: { padding: "16px" } }}
          >
            {filteredHiringTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={filteredHiringTrend} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12, fill: "#666" }}
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: "#666" }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #f0f0f0",
                      borderRadius: "8px",
                    }}
                    formatter={(value) => [value, "Talents Hired"]}
                    labelFormatter={(label) => `${label} ${selectedYear}`}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="#014c75" 
                    radius={[4, 4, 0, 0]}
                    name="Talents Hired"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description={`No hiring data for ${selectedYear}`} style={{ height: 300, display: "flex", flexDirection: "column", justifyContent: "center" }} />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title="Roles Distribution"
            style={{
              borderRadius: "8px",
              border: "1px solid #f0f0f0",
              height: "100%",
            }}
            styles={{ body: { padding: "16px" } }}
          >
            {roleDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={roleDistribution}
                    cx="40%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="role"
                  >
                    {roleDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #f0f0f0",
                      borderRadius: "8px",
                    }}
                    formatter={(value, name) => [value, name]}
                  />
                  <Legend 
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    wrapperStyle={{ paddingLeft: "20px" }}
                    formatter={(value) => <span style={{ color: "#666", fontSize: "12px" }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No role distribution data available" style={{ height: 300, display: "flex", flexDirection: "column", justifyContent: "center" }} />
            )}
          </Card>
        </Col>
      </Row>
    </OverviewWrapper>
  );
};

export default Overview;
